'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';
import { guardActionResult } from '@/shared/entityIds';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import type { PageCastLooks } from '@/contexts/illustrations/services/pageCastLooks';

export async function updatePageCharacters(
  bookId: string,
  pageId: string,
  characterNames: string[],
): Promise<ActionResult<void>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

    const { IllustrationService } = await import('@/contexts/illustrations/services/BookService');
    const { getIllustrationRepository } = await import('@/contexts/illustrations/repositories');
    const { getStoryRepository } = await import('@/contexts/stories/repositories');
    const { getCharacterRepository } = await import('@/contexts/characters/repositories');

    const illustrationService = new IllustrationService(getIllustrationRepository(), getStoryRepository(), getCharacterRepository());
    await illustrationService.updatePageCharacters(bookId, familyId, pageId, characterNames);

    return { success: true, data: undefined };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('updatePageDetails', e) };
  }
}

export async function updatePagePrompt(
  bookId: string,
  pageId: string,
  customPrompt?: string | null,
): Promise<ActionResult<void>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

    const { IllustrationService } = await import('@/contexts/illustrations/services/BookService');
    const { getIllustrationRepository } = await import('@/contexts/illustrations/repositories');
    const { getStoryRepository } = await import('@/contexts/stories/repositories');
    const { getCharacterRepository } = await import('@/contexts/characters/repositories');

    const illustrationService = new IllustrationService(getIllustrationRepository(), getStoryRepository(), getCharacterRepository());
    await illustrationService.updatePagePrompt(bookId, familyId, pageId, customPrompt);

    return { success: true, data: undefined };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('updatePageDetails', e) };
  }
}

/**
 * What a parent may change about a page's picture — and nothing else.
 *
 * The regenerate panel used to print the whole system prompt: hundreds of lines
 * of style lock, art direction, reference-matching rules and text-literalness
 * law. None of it is theirs to change, all of it is ours to keep, and it buried
 * the two things that DO decide what gets drawn: what happens on the page, and
 * who is in frame.
 */
export async function getPageEditable(
  bookId: string,
  pageNumber: number,
): Promise<ActionResult<{
  pageId: string;
  sceneDescription: string;
  characterNames: string[];
  customPrompt: string;
  /** Who THIS picture draws and in which look, from the selection the page job is built from
   *  (pageReferences → pageCastLooks) — plus the book's other characters a parent may add. */
  cast: PageCastLooks;
}>> {
  try {
    const familyId = await SessionService.getFamilyId();
    if (!familyId) return { success: false, error: 'Not authenticated' };

    const { getIllustrationRepository } = await import('@/contexts/illustrations/repositories');
    const book = await getIllustrationRepository().getById(bookId, familyId);
    if (!book) return { success: false, error: 'Book not found' };
    const page = book.pages.find((p) => p.pageNumber === pageNumber);
    if (!page) return { success: false, error: 'Page not found' };

    const { IllustrationGenerationService } = await import('@/contexts/illustrations/services/BookGenerationService');
    const { getCharacterRepository } = await import('@/contexts/characters/repositories');
    const { getTokenManager } = await import('@/contexts/subscriptions/services/getTokenManager');
    const { getImageGenerationQueue } = await import('@/contexts/ai/infrastructure/imageGenerationQueue');
    const { pageCastLooks } = await import('@/contexts/illustrations/services/pageCastLooks');
    const service = new IllustrationGenerationService(
      getIllustrationRepository(),
      getCharacterRepository(),
      getTokenManager(),
      getImageGenerationQueue(),
    );
    // the SAME selection the redraw sends (buildPagePayload reads it too)
    const sel = await service.pageReferences(book, page);

    const stored = book.characterReferences ?? [];
    let cast = pageCastLooks({
      pageNames: sel.pageNames,
      refs: sel.characterRefs,
      wardrobe: sel.pageAnalysis?.continuity?.wardrobe,
      panels: sel.panels,
      analysed: sel.analysed,
      stored,
    });
    let characterNames = page.characterNames ?? [];
    // A book of the new pipeline keeps who is on a page in its scene plan, not on the page row.
    if (cast.rows.length === 0 && characterNames.length === 0 && book.storyId && page.blockNumber != null) {
      const { StoryV2Repository } = await import('@/contexts/pipeline-v2/story/StoryV2Repository');
      const { planPageCast } = await import('@/contexts/illustrations/services/pageCastLooks');
      const parse = await new StoryV2Repository().loadStoryParse(book.storyId).catch(() => null);
      if (parse) {
        cast = planPageCast(parse.bible, parse.scenes, page.blockNumber, stored);
        characterNames = cast.rows.map((r) => r.name);
      }
    }

    // the redraw dialog shows every string of this (entity-id guard, registry plan §4.2)
    return {
      success: true,
      data: guardActionResult({
        pageId: page.id,
        sceneDescription: page.sceneDescription ?? '',
        characterNames,
        customPrompt: page.customPrompt ?? '',
        cast,
      }, { bookId }),
    };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('getPageEditable', e) };
  }
}

/** The scene a page draws — the sentence that decides what is in the picture. */
export async function updatePageScene(
  bookId: string,
  pageId: string,
  sceneDescription: string,
): Promise<ActionResult<void>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

    const { getIllustrationRepository } = await import('@/contexts/illustrations/repositories');
    await getIllustrationRepository().updatePageSceneDescription(
      bookId,
      familyId,
      pageId,
      sceneDescription.trim() || null,
    );
    return { success: true, data: undefined };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('updatePageScene', e) };
  }
}
